import React, { ReactNode, createContext, useCallback, useContext, useRef } from 'react';
import { SharedValue, useSharedValue } from 'react-native-reanimated';

import BottomSheet from '@gorhom/bottom-sheet';

import { PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { usePlayerProfileDataStore } from '../player-profile-sheets/hooks/use-player-profile-data-store';

type SinglePlayerProfileContextValue = {
    playerProfileAnimatedIndex: SharedValue<number>;
    isPlayerProfileModalOpen: SharedValue<boolean>;
    closeSinglePlayerProfile: () => void;
    openSinglePlayerProfile: (player: PlayerWithTeamAndEvent, playerProjectionFilter?: string) => void;
    playerProfileModalRef: React.RefObject<BottomSheet>;
    onSheetChange?: (index: number) => void;
};

const SinglePlayerProfileContext = createContext<SinglePlayerProfileContextValue | undefined>(undefined);

export const SinglePlayerProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const playerProfileAnimatedIndex = useSharedValue(-1);
    const isPlayerProfileModalOpen = useSharedValue(false);
    const playerProfileModalRef = useRef<BottomSheet>(null);

    const openSinglePlayerProfile = useCallback(
        (selectedPlayer: PlayerWithTeamAndEvent, playerProjectionFilter?: string) => {
            usePlayerProfileDataStore.getState().actions.setSelectedPlayerProjectionFilter(playerProjectionFilter);
            usePlayerProfileDataStore.getState().actions.setSelectedSinglePlayer(selectedPlayer);
            playerProfileModalRef.current?.snapToIndex(0);
            isPlayerProfileModalOpen.value = true;
        },
        [isPlayerProfileModalOpen]
    );

    const closeSinglePlayerProfile = useCallback(() => {
        playerProfileModalRef.current?.close();
        isPlayerProfileModalOpen.value = false;
        playerProfileAnimatedIndex.value = -1;
    }, [isPlayerProfileModalOpen, playerProfileAnimatedIndex]);

    return (
        <SinglePlayerProfileContext.Provider
            value={{
                closeSinglePlayerProfile,
                openSinglePlayerProfile,
                playerProfileAnimatedIndex,
                isPlayerProfileModalOpen,
                playerProfileModalRef,
            }}
        >
            {children}
        </SinglePlayerProfileContext.Provider>
    );
};

export const useSinglePlayerProfile = (): SinglePlayerProfileContextValue => {
    const context = useContext(SinglePlayerProfileContext);
    if (!context) {
        throw new Error('usePlayerProfile must be used within a PlayerProfileProvider');
    }
    return context;
};
